import { cn } from "@/utils";
import { Button, Card } from "@mantine/core";
import { AdminFinance } from "@workspace/admin-domain/src/features/finance/schema";
import { AdminWithdrawal } from "@workspace/admin-domain/src/features/withdrawal/schema";
import { t } from "i18next";
import { LucideAlertTriangle } from "lucide-react";

export type LedgerSortBy = AdminFinance["PaymentsFilters"]["sortBy"];
export type WithdrawalSortBy = AdminWithdrawal["GetAll"]["sortBy"];

// Ledger status options
export const LEDGER_STATUS_OPTIONS = [
  { value: "PENDING", label: t("finance.ledger.statuses.PENDING") },
  { value: "CAPTURED", label: t("finance.ledger.statuses.CAPTURED") },
  { value: "REFUNDED", label: t("finance.ledger.statuses.REFUNDED") },
  { value: "FAILED", label: t("finance.ledger.statuses.FAILED") },
  { value: "VOIDED", label: t("finance.ledger.statuses.VOIDED") },
] as const;

// Withdrawal status options
export const WITHDRAWAL_STATUS_OPTIONS = [
  { value: "PENDING", label: t("finance.withdrawals.statuses.PENDING") },
  { value: "COMPLETED", label: t("finance.withdrawals.statuses.COMPLETED") },
  { value: "FAILED", label: t("finance.withdrawals.statuses.FAILED") },
] as const;

// Ledger sort options
export const LEDGER_SORT_OPTIONS: {
  label: string;
  kv: `${LedgerSortBy}_${"asc" | "desc"}`;
}[] = [
  { label: t("sort.date_desc"), kv: "createdAt_desc" },
  { label: t("sort.date_asc"), kv: "createdAt_asc" },
  { label: t("sort.amount_desc"), kv: "amount_desc" },
  { label: t("sort.amount_asc"), kv: "amount_asc" },
];

// Withdrawal sort options
export const WITHDRAWAL_SORT_OPTIONS: {
  label: string;
  kv: `${WithdrawalSortBy}_${"asc" | "desc"}`;
}[] = [
  { label: t("sort.date_desc"), kv: "createdAt_desc" },
  { label: t("sort.date_asc"), kv: "createdAt_asc" },
  { label: t("sort.amount_desc"), kv: "amount_desc" },
  { label: t("sort.amount_asc"), kv: "amount_asc" },
];

export const DEFAULT_LEDGER_PARAMS = {
  page: 0,
  limit: 10,
  sortBy: "createdAt" as const,
  sortOrder: "desc" as const,
};

export const DEFAULT_WITHDRAWAL_PARAMS = {
  page: 0,
  limit: 10,
  sortBy: "createdAt" as const,
  sortOrder: "desc" as const,
};

export function FinanceErrorCard({
  onRetry,
  title,
  className,
}: {
  onRetry?: () => void;
  title?: string;
  className?: string;
}) {
  return (
    <Card
      withBorder
      className={cn("text-red-filled p-lg flex flex-col items-center justify-center text-center", className)}
    >
      <LucideAlertTriangle className="mb-1.5 size-8 opacity-80" />
      <p className="text-center text-sm font-medium">{title ?? t("finance.error.description")}</p>
      {onRetry ? (
        <Button variant="subtle" color="red" size="xs" onClick={onRetry} className="mt-1.5">
          {t("tryAgain")}
        </Button>
      ) : null}
    </Card>
  );
}
